import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Sparkles, Calendar as CalendarIcon, User, Building2, Plus, Trash2, AlertTriangle, Clock, ExternalLink, Users, MoreHorizontal, Mail, Edit, Phone, Wrench, FileText, Send, Paperclip, Upload, Loader2, X
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '../components/ui/hover-card';
import { format, parse } from 'date-fns';
import { nl, uk } from 'date-fns/locale';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import SearchSelect from '../components/SearchSelect';
import CcSelector from '../components/CcSelector';
import DocumentLightbox from '../components/DocumentLightbox';
import api, { cleaningAPI, propertyAPI, customerAPI, installationAPI, appointmentAPI, usersAPI } from '../lib/api';
import { formatDate, needsCleaning, getGoogleMapsUrl, getWazeUrl, formatPhoneNumber } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Cleaning = () => {
  const { t, language } = useLanguage();
  const dateLocale = language === 'uk' ? uk : nl;
  const [cleanings, setCleanings] = useState([]);
  const [properties, setProperties] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [installations, setInstallations] = useState([]);
  const [allAppointments, setAllAppointments] = useState([]); // For calendar hover tooltip
  const [propertiesNeedingCleaning, setPropertiesNeedingCleaning] = useState([]);
  const [propertiesToPlan, setPropertiesToPlan] = useState([]); // Properties with contract but no cleaning date
  const [contractsDue, setContractsDue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [cleaningToDelete, setCleaningToDelete] = useState(null);
  // Calendar popup state
  const [calendarPopupOpen, setCalendarPopupOpen] = useState(false);
  const [selectedCleaningForCalendar, setSelectedCleaningForCalendar] = useState(null);
  const [cleaningEmailAction, setCleaningEmailAction] = useState('queue'); // 'queue', 'send', or 'none'
  const [cleaningCcEmails, setCleaningCcEmails] = useState([]); // CC recipients for cleaning confirmation
  const [sendingEmail, setSendingEmail] = useState(false);
  // Track if property is pre-selected (locked) when opening dialog from specific customer
  const [isPropertyLocked, setIsPropertyLocked] = useState(false);
  // Reschedule popup state for cancelled cleanings
  const [reschedulePopupOpen, setReschedulePopupOpen] = useState(false);
  const [cleaningToReschedule, setCleaningToReschedule] = useState(null);
  const [rescheduleData, setRescheduleData] = useState({
    cleaning_date: '',
    cleaning_time: '09:00',
    send_confirmation: true
  });
  const [rescheduling, setRescheduling] = useState(false);
  // Edit popup state for existing cleanings
  const [editPopupOpen, setEditPopupOpen] = useState(false);
  const [cleaningToEdit, setCleaningToEdit] = useState(null);
  // Foto's & documenten bij de reinigingsafspraak
  const [editPhotos, setEditPhotos] = useState([]);
  const [uploadingEditPhoto, setUploadingEditPhoto] = useState(false);
  const [lightboxDoc, setLightboxDoc] = useState(null);
  const [editData, setEditData] = useState({
    cleaning_date: '',
    cleaning_time: '09:00',
    notes: '',
    send_confirmation: false
  });
  const [editing, setEditing] = useState(false);
  // Customer details popup state
  const [customerPopupOpen, setCustomerPopupOpen] = useState(false);
  const [selectedCustomerData, setSelectedCustomerData] = useState(null);
  const [formData, setFormData] = useState({
    customer_id: '',
    property_id: '',
    cleaning_date: '',
    cleaning_end_date: '',
    cleaning_time: '09:00',
    cleaning_end_time: '12:00',
    performed_by: '',
    assigned_to: [],  // Array of user IDs
    assigned_to_names: [],  // Array of names
    notes: '',
    instruction_photo: null
  });
  const [users, setUsers] = useState([]);  // Users for assignment
  const { hasRole } = useAuth();
  const navigate = useNavigate();

  const handleDismissCleaning = async (propertyId) => {
    try {
      await api.patch(`/properties/${propertyId}/dismiss-cleaning`);
      toast.success('Verwijderd uit reinigingslijst');
      fetchData();
    } catch {
      toast.error('Kon niet verwijderen uit lijst');
    }
  };

  const fetchData = async () => {
    try {
      const [cleanRes, propsRes, custRes, instRes, apptRes] = await Promise.all([
        cleaningAPI.getAll(),
        propertyAPI.getAll(),
        customerAPI.getAll(),
        installationAPI.getAll(),
        appointmentAPI.getAll()
      ]);
      setCleanings(cleanRes.data);
      setProperties(propsRes.data);
      setCustomers(custRes.data);
      setInstallations(instRes.data);
      setAllAppointments(apptRes.data || []);
      
      // Fetch users separately for better error handling
      try {
        const usersRes = await usersAPI.getAll();
        const assignableUsers = (usersRes.data || []).filter(u => 
          ['technician', 'cleaner', 'admin'].includes(u.role)
        );
        setUsers(assignableUsers);
      } catch (userErr) {
        console.log('Could not fetch users:', userErr);
        setUsers([]);
      }

      // Fetch paid cleaning invoices (reiniging facturen)
      let paidCleaningFacturen = [];
      try {
        const facturenRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/facturen`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        if (facturenRes.ok) {
          const facturenData = await facturenRes.json();
          // Filter for paid cleaning invoices
          paidCleaningFacturen = facturenData.filter(f => f.status === 'paid');
        }
      } catch (err) {
        console.log('Could not fetch facturen:', err);
      }

      // Fetch properties with cleaning contracts that are due
      try {
        const dueRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/properties/cleaning-due`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        if (dueRes.ok) {
          const dueData = await dueRes.json();
          setContractsDue(dueData);
        }
      } catch (err) {
        console.log('Could not fetch cleaning due:', err);
      }

      // Identify properties needing cleaning
      const needsClean = [];
      const toPlan = [];
      const processedPropertyIds = new Set(); // Track already added properties
      
      for (const prop of propsRes.data) {
        // Check if property has a cleaning contract (including one-time)
        const hasContract = prop.cleaning_contract === '2_yearly' || prop.cleaning_contract === '3_yearly' || prop.cleaning_contract === 'one_time';
        
        const propCleanings = cleanRes.data.filter(c => c.property_id === prop.id);
        const lastCleaning = propCleanings.length > 0 
          ? propCleanings.reduce((a, b) => a.cleaning_date > b.cleaning_date ? a : b)
          : null;
        
        // If has contract but never had a cleaning - needs to be planned
        if (hasContract && !lastCleaning) {
          if (!prop.cleaning_dismissed_at) {
            toPlan.push({
              ...prop,
              lastCleaning: null,
              source: 'contract'
            });
            processedPropertyIds.add(prop.id);
          }
        }
        // If needs cleaning based on schedule (only for recurring contracts, not one-time)
        else if (prop.cleaning_contract !== 'one_time' && needsCleaning(lastCleaning?.cleaning_date)) {
          if (!(prop.cleaning_dismissed_at && (!lastCleaning || new Date(prop.cleaning_dismissed_at) > new Date(lastCleaning.cleaning_date)))) {
            needsClean.push({
              ...prop,
              lastCleaning: lastCleaning?.cleaning_date
            });
          }
        }
      }
      
      // Add customers with paid cleaning invoices (that haven't been cleaned yet)
      for (const factuur of paidCleaningFacturen) {
        // Find the property linked to this factuur's customer
        const customerProps = propsRes.data.filter(p => p.customer_id === factuur.customer_id);
        for (const prop of customerProps) {
          // Skip if already in the list
          if (processedPropertyIds.has(prop.id)) continue;
          
          // Check if this property has any scheduled/performed cleaning after the factuur date
          const propCleanings = cleanRes.data.filter(c => c.property_id === prop.id);
          const hasRecentCleaning = propCleanings.some(c => 
            new Date(c.cleaning_date) >= new Date(factuur.factuur_date)
          );
          
          if (!hasRecentCleaning) {
            toPlan.push({
              ...prop,
              lastCleaning: null,
              source: 'paid_factuur',
              factuur_number: factuur.factuur_number,
              factuur_date: factuur.factuur_date
            });
            processedPropertyIds.add(prop.id);
          }
        }
      }
      
      // Getekende offertes komen hier NIET meer bij — pas als de reinigingsfactuur betaald is (paid_factuur hierboven)
      setPropertiesNeedingCleaning(needsClean);
      setPropertiesToPlan(toPlan);
    } catch (error) {
      toast.error('Kon reinigingen niet laden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Get number of solar panels for a property
  const getPanelCount = (propertyId) => {
    const propertyInstallations = installations.filter(i => 
      i.property_id === propertyId && 
      i.installation_type === 'solar_panels'
    );
    return propertyInstallations.reduce((sum, inst) => sum + (inst.number_of_panels || 0), 0);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Combine date and time
      const cleaningDateTime = formData.cleaning_time 
        ? `${formData.cleaning_date}T${formData.cleaning_time}`
        : formData.cleaning_date;
      
      const response = await cleaningAPI.create({
        property_id: formData.property_id,
        cleaning_date: cleaningDateTime,
        cleaning_end_date: formData.cleaning_end_date || formData.cleaning_date,
        cleaning_time: formData.cleaning_time,
        cleaning_end_time: formData.cleaning_end_time,
        performed_by: formData.performed_by || (formData.assigned_to_names || []).join(', '),
        assigned_to: formData.assigned_to || [],
        assigned_to_names: formData.assigned_to_names || [],
        notes: formData.notes
      });
      
      // Upload instruction photo if provided
      if (formData.instruction_photo && response.data?.id) {
        const photoFormData = new FormData();
        photoFormData.append('file', formData.instruction_photo);
        
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cleaning/${response.data.id}/instruction-photo`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: photoFormData
        });
      }
      
      toast.success('Reiniging ingepland');
      
      // Send/queue confirmation email if requested
      const cleaningId = response.data?.id || response?.id;
      if (cleaningEmailAction !== 'none' && cleaningId) {
        try {
          const emailResponse = await cleaningAPI.sendConfirmationEmail({
            cleaning_id: cleaningId,
            property_id: formData.property_id,
            cleaning_date: formData.cleaning_date,
            cleaning_time: formData.cleaning_time || '09:00',
            send_confirmation_email: true,
            cc_emails: cleaningCcEmails || []  // Include CC recipients
          });
          
          const emailId = emailResponse?.data?.email_id || emailResponse?.email_id;
          if (cleaningEmailAction === 'send' && emailId) {
            // Send the queued email immediately
            try {
              await fetch(`${API_URL}/api/email-queue/${emailId}/send`, {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${localStorage.getItem('token')}`,
                  'Content-Type': 'application/json'
                }
              });
              toast.success('Bevestigingsmail verzonden!');
            } catch (sendError) {
              console.error('Error sending email:', sendError);
              toast.success('Mail klaargezet in wachtrij');
            }
          } else {
            toast.success('Bevestigingsmail klaargezet in wachtrij');
          }
        } catch (emailError) {
          console.error('Error queueing confirmation email:', emailError);
        }
      }
      
      setDialogOpen(false);
      setFormData({ customer_id: '', property_id: '', cleaning_date: '', cleaning_end_date: '', cleaning_time: '09:00', cleaning_end_time: '12:00', performed_by: '', assigned_to: [], assigned_to_names: [], notes: '', instruction_photo: null });
      setCleaningEmailAction('queue'); // Reset to default
      setCleaningCcEmails([]); // Reset CC emails
      fetchData();
    } catch (error) {
      toast.error('Kon reiniging niet toevoegen');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    setCleaningToDelete(id);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!cleaningToDelete) return;
    try {
      await cleaningAPI.delete(cleaningToDelete);
      toast.success('Reiniging verwijderd');
      fetchData();
    } catch (error) {
      toast.error('Kon niet verwijderen');
    } finally {
      setDeleteConfirmOpen(false);
      setCleaningToDelete(null);
    }
  };

  // Open reschedule popup for cancelled cleaning
  const handleOpenReschedulePopup = (cleaning) => {
    setCleaningToReschedule(cleaning);
    // Set default date to tomorrow
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    setRescheduleData({
      cleaning_date: tomorrow.toISOString().split('T')[0],
      cleaning_time: '09:00',
      send_confirmation: true
    });
    setReschedulePopupOpen(true);
  };

  // Handle reschedule submit
  const handleRescheduleSubmit = async () => {
    if (!cleaningToReschedule || !rescheduleData.cleaning_date) {
      toast.error('Selecteer een datum');
      return;
    }

    setRescheduling(true);
    try {
      // First, update the cleaning with new date/time
      const newCleaningDate = `${rescheduleData.cleaning_date}T${rescheduleData.cleaning_time}`;
      
      // Use a direct API call to update the cleaning
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cleaning/${cleaningToReschedule.id}/reschedule-with-date`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          new_date: newCleaningDate
        })
      });

      if (!response.ok) {
        throw new Error('Could not reschedule');
      }

      // Send confirmation email if requested
      if (rescheduleData.send_confirmation) {
        try {
          await cleaningAPI.sendConfirmationEmail({
            cleaning_id: cleaningToReschedule.id,
            property_id: cleaningToReschedule.property_id,
            cleaning_date: rescheduleData.cleaning_date,
            cleaning_time: rescheduleData.cleaning_time,
            send_confirmation_email: true
          });
          toast.success('Reiniging opnieuw ingepland en bevestigingsmail verzonden!');
        } catch (emailErr) {
          console.error('Email error:', emailErr);
          toast.success('Reiniging opnieuw ingepland (mail kon niet verzonden worden)');
        }
      } else {
        toast.success('Reiniging opnieuw ingepland!');
      }

      setReschedulePopupOpen(false);
      setCleaningToReschedule(null);
      fetchData();
    } catch (err) {
      console.error('Reschedule error:', err);
      toast.error('Kon reiniging niet opnieuw inplannen');
    } finally {
      setRescheduling(false);
    }
  };

  // Open edit popup for existing cleaning
  const handleOpenEditPopup = (cleaning) => {
    setCleaningToEdit(cleaning);
    setEditPhotos(cleaning.photos || []);
    // Parse existing date and time
    let existingDate = cleaning.cleaning_date || '';
    let existingTime = '09:00';
    if (existingDate.includes('T')) {
      const parts = existingDate.split('T');
      existingDate = parts[0];
      existingTime = parts[1]?.substring(0, 5) || '09:00';
    }
    setEditData({
      cleaning_date: existingDate,
      cleaning_time: existingTime,
      notes: cleaning.notes || '',
      send_confirmation: false
    });
    setEditPopupOpen(true);
  };

  // Foto/document upload voor reinigingsafspraak (edit dialog)
  const handleEditPhotoUpload = async (file) => {
    if (!cleaningToEdit || !file) return;
    const fd = new FormData();
    fd.append('target_type', 'cleaning');
    fd.append('target_id', cleaningToEdit.id);
    fd.append('file', file);
    setUploadingEditPhoto(true);
    try {
      const res = await fetch(`${API_URL}/api/appointment-photos/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: fd
      });
      if (!res.ok) throw new Error('Upload mislukt');
      const data = await res.json();
      setEditPhotos(prev => [...prev, data.photo]);
      toast.success('Bestand toegevoegd');
      fetchData();
    } catch (err) {
      toast.error('Kon bestand niet uploaden');
    } finally {
      setUploadingEditPhoto(false);
    }
  };

  const handleEditPhotoDelete = async (photoId) => {
    if (!cleaningToEdit) return;
    try {
      const res = await fetch(`${API_URL}/api/appointment-photos/cleaning/${cleaningToEdit.id}/${photoId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (!res.ok) throw new Error('Verwijderen mislukt');
      setEditPhotos(prev => prev.filter(p => p.id !== photoId));
      toast.success('Bestand verwijderd');
      fetchData();
    } catch (err) {
      toast.error('Kon bestand niet verwijderen');
    }
  };

  // Handle edit submit
  const handleEditSubmit = async () => {
    if (!cleaningToEdit || !editData.cleaning_date) {
      toast.error('Selecteer een datum');
      return;
    }

    setEditing(true);
    try {
      const newCleaningDate = `${editData.cleaning_date}T${editData.cleaning_time}`;
      
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cleaning/${cleaningToEdit.id}/update-date`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          new_date: newCleaningDate,
          notes: editData.notes
        })
      });

      if (!response.ok) {
        throw new Error('Could not update cleaning');
      }

      // Send confirmation email if requested
      if (editData.send_confirmation) {
        try {
          await cleaningAPI.sendConfirmationEmail({
            cleaning_id: cleaningToEdit.id,
            property_id: cleaningToEdit.property_id,
            cleaning_date: editData.cleaning_date,
            cleaning_time: editData.cleaning_time,
            send_confirmation_email: true
          });
          toast.success('Afspraak bijgewerkt en bevestigingsmail verzonden!');
        } catch (emailErr) {
          console.error('Email error:', emailErr);
          toast.success('Afspraak bijgewerkt (mail kon niet verzonden worden)');
        }
      } else {
        toast.success('Afspraak bijgewerkt!');
      }

      setEditPopupOpen(false);
      setCleaningToEdit(null);
      fetchData();
    } catch (err) {
      console.error('Edit error:', err);
      toast.error('Kon afspraak niet bijwerken');
    } finally {
      setEditing(false);
    }
  };

  const getPropertyAddress = (propertyId) => {
    const prop = properties.find(p => p.id === propertyId);
    return prop ? `${prop.street} ${prop.number}, ${prop.city}` : '';
  };

  const getProperty = (propertyId) => {
    return properties.find(p => p.id === propertyId);
  };

  const getCustomerForProperty = (propertyId) => {
    const prop = properties.find(p => p.id === propertyId);
    if (!prop) return null;
    return customers.find(c => c.id === prop.customer_id);
  };

  // Open customer details popup
  const handleOpenCustomerPopup = (cleaning) => {
    const property = properties.find(p => p.id === cleaning.property_id);
    const customer = property ? customers.find(c => c.id === property.customer_id) : null;
    
    setSelectedCustomerData({
      customer,
      property,
      cleaning
    });
    setCustomerPopupOpen(true);
  };

  const getCleaningTime = (cleaningDate) => {
    if (!cleaningDate) return '';
    // Check if the date string contains a time component
    if (cleaningDate.includes('T')) {
      const timePart = cleaningDate.split('T')[1];
      if (timePart) {
        return timePart.substring(0, 5); // Get HH:MM
      }
    }
    return '';
  };

  // Get formatted time range (e.g., "09:00 - 12:00")
  const getCleaningTimeRange = (cleaning) => {
    const startTime = getCleaningTime(cleaning.cleaning_date);
    const endTime = cleaning.cleaning_end_time || '';
    if (startTime && endTime) {
      return `${startTime} - ${endTime}`;
    }
    return startTime || '';
  };

  // Generate Google Calendar URL for a cleaning
  const getGoogleCalendarUrl = (cleaning) => {
    const property = getProperty(cleaning.property_id);
    const customer = getCustomerForProperty(cleaning.property_id);
    
    if (!property) return null;
    
    // Parse the cleaning date and time
    const cleaningDate = cleaning.cleaning_date;
    let startDate, endDate;
    
    if (cleaningDate.includes('T')) {
      // Has time component
      const dateObj = new Date(cleaningDate);
      startDate = dateObj.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
      // End time is 2 hours later
      const endDateObj = new Date(dateObj.getTime() + 2 * 60 * 60 * 1000);
      endDate = endDateObj.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
    } else {
      // Date only - make it an all-day event
      const dateStr = cleaningDate.replace(/-/g, '');
      startDate = dateStr;
      endDate = dateStr;
    }
    
    const title = encodeURIComponent(`Reiniging - ${customer?.first_name || ''} ${customer?.last_name || ''}`);
    const location = encodeURIComponent(`${property.street} ${property.number}, ${property.postal_code} ${property.city}`);
    
    // Get panel count
    const panelCount = getPanelCount(cleaning.property_id);
    
    let description = `Zonnepanelen reiniging\n`;
    if (panelCount > 0) {
      description += `\n🔆 ${panelCount} zonnepanelen te reinigen\n`;
    }
    if (customer) {
      description += `\nKlant: ${customer.first_name} ${customer.last_name}\n`;
      if (customer.phone) description += `Tel: ${customer.phone}\n`;
      if (customer.email) description += `Email: ${customer.email}\n`;
    }
    description += `\nAdres: ${property.street} ${property.number}, ${property.postal_code} ${property.city}`;
    if (cleaning.notes) description += `\n\nNotities: ${cleaning.notes}`;
    if (cleaning.performed_by) description += `\n\nUitgevoerd door: ${cleaning.performed_by}`;
    
    const descriptionEncoded = encodeURIComponent(description);
    
    return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${startDate}/${endDate}&location=${location}&details=${descriptionEncoded}`;
  };

  // Handle clicking "Naar Google Calendar" - opens popup first
  const handleCalendarClick = (cleaning) => {
    setSelectedCleaningForCalendar(cleaning);
    setSendConfirmationEmail(true); // Default to checked
    setCalendarPopupOpen(true);
  };

  // Handle the confirmation popup submit
  const handleCalendarConfirm = async () => {
    if (!selectedCleaningForCalendar) return;
    
    const calendarUrl = getGoogleCalendarUrl(selectedCleaningForCalendar);
    
    // If user wants to send confirmation email, call the webhook
    if (sendConfirmationEmail) {
      setSendingEmail(true);
      try {
        // Extract date and time from cleaning_date
        const cleaningDate = selectedCleaningForCalendar.cleaning_date;
        let date = cleaningDate;
        let time = '09:00';
        
        if (cleaningDate.includes('T')) {
          const parts = cleaningDate.split('T');
          date = parts[0];
          time = parts[1]?.substring(0, 5) || '09:00';
        }
        
        await cleaningAPI.sendConfirmationEmail({
          cleaning_id: selectedCleaningForCalendar.id,
          property_id: selectedCleaningForCalendar.property_id,
          cleaning_date: date,
          cleaning_time: time,
          send_confirmation_email: true
        });
        
        toast.success('Email klaargezet in Mails > Klaargezet');
      } catch (error) {
        console.error('Error sending confirmation email:', error);
        toast.error('Kon bevestigingsmail niet klaarzetten');
      } finally {
        setSendingEmail(false);
      }
    }
    
    // Close popup and open Google Calendar
    setCalendarPopupOpen(false);
    setSelectedCleaningForCalendar(null);
    
    // Open Google Calendar in new tab
    if (calendarUrl) {
      window.open(calendarUrl, '_blank');
    }
  };

  // Build customer options for search select
  const customerOptions = customers.map(cust => ({
    value: cust.id,
    label: `${cust.first_name} ${cust.last_name}`,
    email: cust.email || ''
  }));

  // Prepare property options for search select - include panel count, filter by customer if selected
  const propertyOptions = properties
    .filter(p => !formData.customer_id || p.customer_id === formData.customer_id)
    .map(p => {
      const panelCount = getPanelCount(p.id);
      const customer = customers.find(c => c.id === p.customer_id);
      return {
        value: p.id,
        label: `${p.street} ${p.number}, ${p.postal_code} ${p.city}${panelCount > 0 ? ` (${panelCount} panelen)` : ''}`,
        customer: customer ? `${customer.first_name} ${customer.last_name}` : '',
        customer_id: p.customer_id
      };
    });

  // Helper for date formatting (local timezone)
  const formatLocalDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Helper for calendar hover tooltip
  const getAppointmentsForDate = (date) => {
    const dateStr = formatLocalDate(date);
    return allAppointments.filter(a => a.date === dateStr);
  };

  // Helper to format duration
  const formatDuration = (minutes) => {
    if (!minutes) return '';
    if (minutes >= 60) {
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      return mins > 0 ? `${hours}u${mins}` : `${hours}u`;
    }
    return `${minutes}min`;
  };

  // Helper to get city from appointment
  const getAppointmentCity = (appointment) => {
    if (appointment.property_id) {
      const prop = properties.find(p => p.id === appointment.property_id);
      if (prop?.city) return prop.city;
    }
    if (appointment.customer_id) {
      const cust = customers.find(c => c.id === appointment.customer_id);
      if (cust?.city) return cust.city;
    }
    return null;
  };

  // Custom day content with hover tooltip
  const DayWithTooltip = ({ date }) => {
    const dayAppointments = getAppointmentsForDate(date);
    const hasAppointments = dayAppointments.length > 0;
    
    if (!hasAppointments) {
      return <span>{date.getDate()}</span>;
    }

    const cleaningsForDay = dayAppointments.filter(a => a.appointment_type === 'cleaning');
    const sales = dayAppointments.filter(a => a.appointment_type === 'sales');
    const installationsForDay = dayAppointments.filter(a => a.appointment_type === 'installation');

    // Render appointment info with duration and city
    const renderAppointment = (a, i) => {
      const duration = formatDuration(a.duration_minutes);
      const city = getAppointmentCity(a);
      const timeStr = a.end_time ? `${a.time}-${a.end_time}` : a.time;
      return (
        <div key={i} className="text-xs pl-4 text-muted-foreground space-y-0.5">
          <div className="flex items-center gap-1 flex-wrap">
            <span className="font-medium">{timeStr}</span>
            {duration && <span className="text-muted-foreground/70">({duration})</span>}
            <span>-</span>
            <span>{a.customer_name || a.title}</span>
            {city && <span className="text-blue-400/80">• {city}</span>}
          </div>
          {a.assigned_to_name && <div className="text-primary/70">→ {a.assigned_to_name}</div>}
        </div>
      );
    };

    return (
      <HoverCard openDelay={200} closeDelay={100}>
        <HoverCardTrigger asChild>
          <span className="cursor-pointer font-bold">{date.getDate()}</span>
        </HoverCardTrigger>
        <HoverCardContent className="w-80 p-3" side="right" align="start">
          <div className="space-y-2">
            <p className="font-semibold text-sm border-b pb-1">
              {format(date, 'd MMMM yyyy', { locale: nl })}
            </p>
            <p className="text-xs text-muted-foreground">{dayAppointments.length} afspraken</p>
            
            {cleaningsForDay.length > 0 && (
              <div className="space-y-1">
                <div className="flex items-center gap-1 text-xs font-medium text-purple-400">
                  <Sparkles className="w-3 h-3" />
                  Reinigingen ({cleaningsForDay.length})
                </div>
                {cleaningsForDay.slice(0, 3).map((a, i) => renderAppointment(a, i))}
                {cleaningsForDay.length > 3 && <div className="text-xs pl-4 text-muted-foreground">+{cleaningsForDay.length - 3} meer...</div>}
              </div>
            )}
            
            {sales.length > 0 && (
              <div className="space-y-1">
                <div className="flex items-center gap-1 text-xs font-medium text-blue-400">
                  <Users className="w-3 h-3" />
                  Sales ({sales.length})
                </div>
                {sales.slice(0, 3).map((a, i) => renderAppointment(a, i))}
                {sales.length > 3 && <div className="text-xs pl-4 text-muted-foreground">+{sales.length - 3} meer...</div>}
              </div>
            )}
            
            {installationsForDay.length > 0 && (
              <div className="space-y-1">
                <div className="flex items-center gap-1 text-xs font-medium text-amber-400">
                  <Wrench className="w-3 h-3" />
                  Installaties ({installationsForDay.length})
                </div>
                {installationsForDay.slice(0, 3).map((a, i) => renderAppointment(a, i))}
                {installationsForDay.length > 3 && <div className="text-xs pl-4 text-muted-foreground">+{installationsForDay.length - 3} meer...</div>}
              </div>
            )}
          </div>
        </HoverCardContent>
      </HoverCard>
    );
  };

  return (
    <div className="space-y-4 sm:space-y-6 animate-fade-in max-w-full overflow-x-hidden px-2 sm:px-0" data-testid="cleaning-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-4xl font-bold tracking-tight">{t('cleaningTitle')}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t('cleaningDescription')}</p>
        </div>
        {hasRole(['admin', 'technician']) && (
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) {
              setIsPropertyLocked(false);
            }
          }}>
            <DialogTrigger asChild>
              <Button data-testid="add-cleaning-btn" onClick={() => setIsPropertyLocked(false)} size="sm">
                <Plus className="w-4 h-4 mr-2" />
                <span className="hidden sm:inline">{t('scheduleCleaning')}</span>
                <span className="sm:hidden">{t('schedule')}</span>
              </Button>
            </DialogTrigger>
            <DialogContent className="max-h-[90vh] overflow-y-auto max-w-[calc(100vw-2rem)] sm:max-w-lg">
              <DialogHeader>
                <DialogTitle>{t('scheduleCleaning')}</DialogTitle>
                <DialogDescription>{t('noCleaningsDesc')}</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4">
                {/* Klant selectie */}
                {!isPropertyLocked && (
                  <div className="space-y-2">
                    <Label>Klant</Label>
                    <SearchSelect
                      options={customerOptions}
                      value={formData.customer_id}
                      onChange={(value) => setFormData(prev => ({ ...prev, customer_id: value, property_id: '' }))}
                      placeholder="Zoek klant op naam..."
                      displayKey="label"
                      valueKey="value"
                      renderOption={(opt) => (
                        <div>
                          <div className="font-medium">{opt.label}</div>
                          {opt.email && (
                            <div className="text-xs text-muted-foreground">{opt.email}</div>
                          )}
                        </div>
                      )}
                    />
                  </div>
                )}
                
                {/* Adres selectie */}
                <div className="space-y-2">
                  <Label>{t('address')} *</Label>
                  {isPropertyLocked && formData.property_id ? (
                    // Show locked property as text
                    (() => {
                      const selectedProperty = properties.find(p => p.id === formData.property_id);
                      const customer = selectedProperty ? customers.find(c => c.id === selectedProperty.customer_id) : null;
                      const panelCount = selectedProperty ? getPanelCount(selectedProperty.id) : 0;
                      return (
                        <div className="p-3 rounded-md border bg-muted/50">
                          <p className="font-medium">
                            {customer?.first_name} {customer?.last_name}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {selectedProperty?.street} {selectedProperty?.number}, {selectedProperty?.postal_code} {selectedProperty?.city}
                            {panelCount > 0 && ` (${panelCount} ${t('panels')})`}
                          </p>
                        </div>
                      );
                    })()
                  ) : (
                    <SearchSelect
                      options={propertyOptions}
                      value={formData.property_id}
                      onChange={(value) => {
                        const selectedProp = propertyOptions.find(p => p.value === value);
                        setFormData(prev => ({ 
                          ...prev, 
                          property_id: value,
                          customer_id: selectedProp?.customer_id || prev.customer_id
                        }));
                      }}
                      placeholder={t('searchAddress')}
                      displayKey="label"
                      valueKey="value"
                      renderOption={(opt) => (
                        <div>
                          <div className="font-medium">{opt.label}</div>
                          {opt.customer && !formData.customer_id && (
                            <div className="text-xs text-muted-foreground">{opt.customer}</div>
                          )}
                        </div>
                      )}
                    />
                  )}
                  {formData.customer_id && propertyOptions.length === 0 && !isPropertyLocked && (
                    <p className="text-xs text-amber-500">Deze klant heeft nog geen adressen</p>
                  )}
                </div>
                {/* Datum rij */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t('date')} *</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start text-left font-normal">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {formData.cleaning_date ? format(parse(formData.cleaning_date, 'yyyy-MM-dd', new Date()), 'd MMM yyyy', { locale: dateLocale }) : t('selectDate')}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={formData.cleaning_date ? parse(formData.cleaning_date, 'yyyy-MM-dd', new Date()) : undefined}
                          onSelect={(date) => date && setFormData(prev => ({ ...prev, cleaning_date: format(date, 'yyyy-MM-dd') }))}
                          locale={nl}
                          initialFocus
                          components={{
                            DayContent: ({ date }) => <DayWithTooltip date={date} />
                          }}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className="space-y-2">
                    <Label>{t('endDate')} <span className="text-muted-foreground text-xs">({t('optional')})</span></Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start text-left font-normal">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {formData.cleaning_end_date ? format(parse(formData.cleaning_end_date, 'yyyy-MM-dd', new Date()), 'd MMM yyyy', { locale: dateLocale }) : t('sameDay')}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={formData.cleaning_end_date ? parse(formData.cleaning_end_date, 'yyyy-MM-dd', new Date()) : undefined}
                          onSelect={(date) => date && setFormData(prev => ({ ...prev, cleaning_end_date: format(date, 'yyyy-MM-dd') }))}
                          locale={dateLocale}
                          disabled={(date) => formData.cleaning_date && date < parse(formData.cleaning_date, 'yyyy-MM-dd', new Date())}
                          initialFocus
                          components={{
                            DayContent: ({ date }) => <DayWithTooltip date={date} />
                          }}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>

                {/* Tijd rij */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t('startTime')} *</Label>
                    <Input
                      type="time"
                      value={formData.cleaning_time}
                      onChange={(e) => setFormData(prev => ({ ...prev, cleaning_time: e.target.value }))}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('endTime')} *</Label>
                    <Input
                      type="time"
                      value={formData.cleaning_end_time}
                      onChange={(e) => setFormData(prev => ({ ...prev, cleaning_end_time: e.target.value }))}
                      required
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>{t('performedBy')}</Label>
                  <div className="border rounded-md p-3 space-y-2 max-h-[150px] overflow-y-auto">
                    {users.length === 0 ? (
                      <p className="text-sm text-muted-foreground">Geen Install/Clean gebruikers beschikbaar</p>
                    ) : (
                      users.map(u => {
                        const isSelected = (formData.assigned_to || []).includes(u.id);
                        const roleLabel = u.role === 'cleaner' ? 'Install/Clean' : u.role;
                        return (
                          <div key={u.id} className="flex items-center gap-2">
                            <Checkbox
                              id={`cleaning-user-${u.id}`}
                              checked={isSelected}
                              onCheckedChange={(checked) => {
                                const currentIds = formData.assigned_to || [];
                                const currentNames = formData.assigned_to_names || [];
                                const userName = `${u.first_name} ${u.last_name}`;
                                
                                if (checked) {
                                  setFormData(prev => ({
                                    ...prev,
                                    assigned_to: [...currentIds, u.id],
                                    assigned_to_names: [...currentNames, userName],
                                    performed_by: [...currentNames, userName].join(', ')
                                  }));
                                } else {
                                  const idx = currentIds.indexOf(u.id);
                                  const newNames = currentNames.filter((_, i) => i !== idx);
                                  setFormData(prev => ({
                                    ...prev,
                                    assigned_to: currentIds.filter(id => id !== u.id),
                                    assigned_to_names: newNames,
                                    performed_by: newNames.join(', ')
                                  }));
                                }
                              }}
                            />
                            <label 
                              htmlFor={`cleaning-user-${u.id}`} 
                              className="text-sm cursor-pointer flex-1"
                            >
                              {u.first_name} {u.last_name}
                              <span className="text-muted-foreground ml-1">({roleLabel})</span>
                            </label>
                          </div>
                        );
                      })
                    )}
                  </div>
                  {(formData.assigned_to_names || []).length > 0 && (
                    <p className="text-xs text-muted-foreground">
                      Geselecteerd: {(formData.assigned_to_names || []).join(', ')}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>{t('notes')}</Label>
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                    rows={2}
                    placeholder={t('extraNotesForCleaner')}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('instructionPhoto')} ({t('optional')})</Label>
                  <p className="text-xs text-muted-foreground">
                    Voeg een foto toe zodat de kuisman bv. de wateraansluiting kan zien
                  </p>
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={(e) => setFormData(prev => ({ ...prev, instruction_photo: e.target.files[0] || null }))}
                    className="cursor-pointer"
                  />
                  {formData.instruction_photo && (
                    <p className="text-xs text-green-500">
                      ✓ {formData.instruction_photo.name}
                    </p>
                  )}
                </div>
                
                {/* Email Options */}
                <div className="space-y-3">
                  <Label className="text-sm font-medium">Bevestigingsmail</Label>
                  <div className="space-y-2">
                    <div 
                      className={`flex items-start space-x-3 p-3 rounded-lg border cursor-pointer transition-colors ${cleaningEmailAction === 'queue' ? 'bg-blue-500/10 border-blue-500' : 'hover:bg-muted/50'}`}
                      onClick={() => setCleaningEmailAction('queue')}
                    >
                      <div className={`w-4 h-4 mt-0.5 rounded-full border-2 flex items-center justify-center ${cleaningEmailAction === 'queue' ? 'border-blue-500' : 'border-muted-foreground'}`}>
                        {cleaningEmailAction === 'queue' && <div className="w-2 h-2 rounded-full bg-blue-500" />}
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium">Mail klaarzetten</p>
                        <p className="text-xs text-muted-foreground">Mail komt in de wachtrij</p>
                      </div>
                    </div>
                    <div 
                      className={`flex items-start space-x-3 p-3 rounded-lg border cursor-pointer transition-colors ${cleaningEmailAction === 'send' ? 'bg-green-500/10 border-green-500' : 'hover:bg-muted/50'}`}
                      onClick={() => setCleaningEmailAction('send')}
                    >
                      <div className={`w-4 h-4 mt-0.5 rounded-full border-2 flex items-center justify-center ${cleaningEmailAction === 'send' ? 'border-green-500' : 'border-muted-foreground'}`}>
                        {cleaningEmailAction === 'send' && <div className="w-2 h-2 rounded-full bg-green-500" />}
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium">Mail direct verzenden</p>
                        <p className="text-xs text-muted-foreground">Mail wordt meteen verstuurd</p>
                      </div>
                    </div>
                    <div 
                      className={`flex items-start space-x-3 p-3 rounded-lg border cursor-pointer transition-colors ${cleaningEmailAction === 'none' ? 'bg-muted/50 border-muted-foreground' : 'hover:bg-muted/50'}`}
                      onClick={() => setCleaningEmailAction('none')}
                    >
                      <div className={`w-4 h-4 mt-0.5 rounded-full border-2 flex items-center justify-center ${cleaningEmailAction === 'none' ? 'border-muted-foreground' : 'border-muted-foreground'}`}>
                        {cleaningEmailAction === 'none' && <div className="w-2 h-2 rounded-full bg-muted-foreground" />}
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-muted-foreground">Geen mail versturen</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* CC Selector - only show when email will be sent */}
                {cleaningEmailAction !== 'none' && (
                  <CcSelector
                    selectedEmails={cleaningCcEmails}
                    onChange={setCleaningCcEmails}
                    defaultEnabled={true}
                  />
                )}
                
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => {
                    setDialogOpen(false);
                    setIsPropertyLocked(false);
                  }}>Annuleren</Button>
                  <Button type="submit" disabled={saving || !formData.property_id}>
                    {saving ? 'Opslaan...' : 'Inplannen'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Contracts Due for Cleaning - URGENT */}
      {contractsDue.length > 0 && (
        <Card className="border-red-500/30 bg-red-500/5">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="w-5 h-5 text-red-400" />
              <h3 className="font-medium text-red-400">
                ! DRINGEND ! - {contractsDue.length} Reinigingscontracten Te Plannen
              </h3>
            </div>
            <p className="text-xs text-muted-foreground mb-3">
              Deze klanten hebben een 2- of 3-jaarlijks reinigingscontract en moeten binnen 1 maand gereinigd worden.
            </p>
            <div className="space-y-2">
              {contractsDue.map((item) => (
                <div 
                  key={item.property.id} 
                  className={`p-3 rounded-md border cursor-pointer hover:bg-muted/50 transition-colors ${
                    item.is_overdue ? 'border-red-500 bg-red-500/10' : 'border-amber-500/50 bg-amber-500/5'
                  }`}
                  onClick={() => navigate(`/properties/${item.property.id}`)}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">
                          {item.customer?.first_name} {item.customer?.last_name}
                        </span>
                        <Badge variant="outline" className={item.contract_type === '2_yearly' ? 'border-amber-500 text-amber-500' : 'border-blue-500 text-blue-500'}>
                          {item.contract_type === '2_yearly' ? '2-jaarlijks' : '3-jaarlijks'}
                        </Badge>
                        {item.is_overdue && (
                          <Badge className="bg-red-500 text-white">VERLOPEN</Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {item.property.street} {item.property.number}, {item.property.city}
                      </p>
                    </div>
                    <div className="text-right text-sm">
                      <p className="text-muted-foreground">Laatste reiniging:</p>
                      <p className="font-medium">{formatDate(item.last_cleaning)}</p>
                      <p className={`text-xs ${item.is_overdue ? 'text-red-400' : 'text-amber-400'}`}>
                        Deadline: {formatDate(item.next_due)}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* In te plannen - Properties with contract but no cleaning history */}
      {propertiesToPlan.length > 0 && (
        <Card className="border-amber-500/30 bg-amber-500/5">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <CalendarIcon className="w-5 h-5 text-amber-400" />
              <h3 className="font-medium text-amber-400">
                In te plannen - {propertiesToPlan.length} klant(en)
              </h3>
            </div>
            <p className="text-xs text-muted-foreground mb-3">
              Klanten met een reinigingscontract of betaalde factuur die nog geen reiniging hebben gepland.
            </p>
            <div className="space-y-2">
              {propertiesToPlan.map((prop) => {
                const customer = customers.find(c => c.id === prop.customer_id);
                return (
                  <div 
                    key={prop.id} 
                    className="p-3 rounded-md border border-amber-500/30 bg-amber-500/5 cursor-pointer hover:bg-amber-500/10 transition-colors"
                    onClick={() => navigate(`/properties/${prop.id}`)}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">
                            {customer?.first_name} {customer?.last_name}
                          </span>
                          {prop.source === 'paid_factuur' ? (
                            <Badge variant="outline" className="border-emerald-500 text-emerald-500">
                              Factuur betaald
                            </Badge>
                          ) : (
                            (() => {
                              const type = prop.cleaning_contract || prop.offerte_contract_type;
                              const label = type === 'one_time' ? 'Eenmalige reiniging' :
                                type === '2_yearly' ? '2-jaarlijkse reiniging' :
                                type === '3_yearly' ? '3-jaarlijkse reiniging' :
                                'Reiniging';
                              const cls = type === '2_yearly' ? 'border-amber-500 text-amber-500' :
                                type === '3_yearly' ? 'border-blue-500 text-blue-500' :
                                'border-green-500 text-green-500';
                              return (
                                <Badge variant="outline" className={cls} data-testid={`toplan-type-badge-${prop.id}`}>
                                  {label}
                                </Badge>
                              );
                            })()
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {prop.street} {prop.number}, {prop.postal_code} {prop.city}
                        </p>
                        {prop.source === 'paid_factuur' && prop.factuur_number && (
                          <p className="text-xs text-emerald-500">
                            {prop.factuur_number} - betaald
                          </p>
                        )}
                      </div>
                      <div className="text-right flex items-center gap-1">
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="border-amber-500/50 text-amber-500 hover:bg-amber-500/10"
                          onClick={(e) => {
                            e.stopPropagation();
                            setFormData(prev => ({ ...prev, property_id: prop.id }));
                            setIsPropertyLocked(true);
                            setDialogOpen(true);
                          }}
                        >
                          <CalendarIcon className="w-4 h-4 mr-1" />
                          Inplannen
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-500 hover:text-red-600 hover:bg-red-500/10"
                          title="Verwijderen uit reinigingslijst"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDismissCleaning(prop.id);
                          }}
                          data-testid={`dismiss-cleaning-${prop.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Geannuleerde reinigingen - opnieuw inplannen */}
      {cleanings.filter(c => c.status === 'cancelled').length > 0 && (
        <Card className="border-red-500/30 bg-red-500/5">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="w-5 h-5 text-red-400" />
              <h3 className="font-medium text-red-400">
                Opnieuw in te plannen wegens uitstel ({cleanings.filter(c => c.status === 'cancelled').length})
              </h3>
            </div>
            <p className="text-xs text-muted-foreground mb-3">
              Deze reinigingen zijn uitgesteld door een kuisman en moeten opnieuw worden ingepland.
            </p>
            <div className="space-y-3">
              {[...cleanings]
                .filter(c => c.status === 'cancelled')
                .sort((a, b) => new Date(a.cleaning_date) - new Date(b.cleaning_date))
                .map((cleaning) => {
                const property = properties.find(p => p.id === cleaning.property_id);
                const customer = property ? customers.find(c => c.id === property.customer_id) : null;
                const panelCount = getPanelCount(cleaning.property_id);
                
                return (
                  <div 
                    key={cleaning.id} 
                    className="p-4 rounded-lg border border-red-500/30 bg-card"
                  >
                    <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                      <div className="flex-1">
                        {/* Klant naam */}
                        <div className="flex items-center gap-2 flex-wrap mb-2">
                          <User className="w-4 h-4 text-muted-foreground" />
                          <span className="font-semibold text-lg">
                            {customer?.first_name} {customer?.last_name}
                          </span>
                          {panelCount > 0 && (
                            <Badge variant="outline" className="border-purple-500/50 text-purple-400">
                              {panelCount} panelen
                            </Badge>
                          )}
                        </div>
                        
                        {/* Adres */}
                        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                          <Building2 className="w-4 h-4" />
                          <span>{property?.street} {property?.number}, {property?.postal_code} {property?.city}</span>
                        </div>
                        
                        {/* Originele datum */}
                        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                          <Clock className="w-4 h-4" />
                          <span>Originele datum: {formatDate(cleaning.cleaning_date)}</span>
                        </div>
                        
                        {/* Wie heeft geannuleerd */}
                        {cleaning.cancelled_by_name && (
                          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                            <Users className="w-4 h-4" />
                            <span>Uitgesteld door: {cleaning.cancelled_by_name}</span>
                          </div>
                        )}
                        
                        {/* Reden */}
                        {cleaning.cancel_reason && (
                          <div className="mt-2 p-2 rounded bg-red-500/10 border border-red-500/20">
                            <p className="text-sm text-red-400">
                              <strong>Reden:</strong> {cleaning.cancel_reason}
                            </p>
                          </div>
                        )}
                      </div>
                      
                      {/* Acties */}
                      <div className="flex flex-col gap-2">
                        <Button 
                          size="sm" 
                          className="bg-primary hover:bg-primary/90"
                          onClick={() => handleOpenReschedulePopup(cleaning)}
                        >
                          <CalendarIcon className="w-4 h-4 mr-2" />
                          Afspraak inplannen
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => handleOpenCustomerPopup(cleaning)}
                        >
                          <User className="w-4 h-4 mr-2" />
                          Klantgegevens
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Properties Needing Cleaning */}
      {propertiesNeedingCleaning.length > 0 && (
        <Card className="border-amber-500/30 bg-amber-500/5">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="w-5 h-5 text-amber-400" />
              <h3 className="font-medium text-amber-400">
                {propertiesNeedingCleaning.length} Adressen Hebben Reiniging Nodig
              </h3>
            </div>
            <div className="flex flex-wrap gap-2">
              {propertiesNeedingCleaning.slice(0, 5).map((p) => (
                <Badge 
                  key={p.id} 
                  variant="outline" 
                  className="bg-amber-500/20 text-amber-300 border-amber-500/30 cursor-pointer hover:bg-amber-500/30"
                  onClick={() => navigate(`/properties/${p.id}`)}
                >
                  {p.street} {p.number}, {p.city}
                  {p.lastCleaning && <span className="ml-1 text-xs opacity-70">({formatDate(p.lastCleaning)})</span>}
                </Badge>
              ))}
              {propertiesNeedingCleaning.length > 5 && (
                <Badge variant="outline" className="bg-muted text-muted-foreground">
                  +{propertiesNeedingCleaning.length - 5} meer
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Cleaning Schedule (CRM) */}
      <div>
        <h2 className="text-xl font-semibold mb-4">Reinigingsschema (CRM)</h2>
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3, 4].map((i) => (
              <Card key={i} className="h-20 animate-pulse bg-muted" />
            ))}
          </div>
        ) : cleanings.length === 0 ? (
          <Card className="p-12 text-center">
            <Sparkles className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
            <p className="text-lg font-medium text-muted-foreground">Nog geen reinigingen</p>
          </Card>
        ) : (
          <div className="space-y-3">
            {[...cleanings]
              .sort((a, b) => {
                // First compare dates
                const dateA = new Date(a.cleaning_date);
                const dateB = new Date(b.cleaning_date);
                if (dateA.toDateString() !== dateB.toDateString()) {
                  return dateA - dateB;
                }
                // If same date, compare times from cleaning_date
                return dateA - dateB;
              })
              .map((c) => {
              const customer = getCustomerForProperty(c.property_id);
              const cleaningTimeRange = getCleaningTimeRange(c);
              return (
                <Card key={c.id} className="border-border/50" data-testid={`cleaning-card-${c.id}`}>
                  <CardContent className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-md bg-purple-500/20 flex items-center justify-center">
                        <Sparkles className="w-5 h-5 text-purple-400" />
                      </div>
                      <div>
                        <p className="font-medium">Paneel Reiniging</p>
                        <div className="flex items-center gap-4 text-xs text-muted-foreground mt-1 flex-wrap">
                          <span className="flex items-center gap-1">
                            <CalendarIcon className="w-3 h-3" />
                            {formatDate(c.cleaning_date)}
                            {c.cleaning_end_date && c.cleaning_end_date !== c.cleaning_date?.split('T')[0] && (
                              <span className="text-primary"> - {formatDate(c.cleaning_end_date)}</span>
                            )}
                          </span>
                          {cleaningTimeRange && (
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {cleaningTimeRange}
                            </span>
                          )}
                          {(c.assigned_to_names && c.assigned_to_names.length > 0) ? (
                            <span className="flex items-center gap-1 text-primary">
                              <User className="w-3 h-3" />
                              {c.assigned_to_names.join(', ')}
                            </span>
                          ) : c.performed_by && (
                            <span className="flex items-center gap-1">
                              <User className="w-3 h-3" />
                              {c.performed_by}
                            </span>
                          )}
                          {customer && (
                            <span 
                              className="flex items-center gap-1 text-primary cursor-pointer hover:underline"
                              onClick={() => navigate(`/customers/${customer.id}`)}
                            >
                              <Users className="w-3 h-3" />
                              {customer.first_name} {customer.last_name}
                            </span>
                          )}
                          <span 
                            className="flex items-center gap-1 text-muted-foreground cursor-pointer hover:text-primary hover:underline"
                            onClick={() => navigate(`/properties/${c.property_id}`)}
                          >}
                          >
                            <Building2 className="w-3 h-3" />
                            {getPropertyAddress(c.property_id)}
                            {getPanelCount(c.property_id) > 0 && (
                              <Badge variant="outline" className="ml-1 text-xs">
                                {getPanelCount(c.property_id)} panelen
                              </Badge>
                            )}
                          </span>
                          {getProperty(c.property_id) && (
                            <a
                              href={getGoogleMapsUrl(getProperty(c.property_id))}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-1 text-muted-foreground hover:text-primary"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <ExternalLink className="w-3 h-3" />
                              Maps
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {c.notes && <p className="text-sm text-muted-foreground max-w-xs truncate">{c.notes}</p>}
                      
                      {/* Dropdown menu with options */}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem 
                            className="flex items-center cursor-pointer"
                            onClick={() => handleOpenEditPopup(c)}
                          >
                            <Edit className="w-4 h-4 mr-2" />
                            Bewerken
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            className="flex items-center cursor-pointer"
                            onClick={() => handleCalendarClick(c)}
                          >
                            <CalendarIcon className="w-4 h-4 mr-2" />
                            Naar Google Calendar
                          </DropdownMenuItem>
                          {getProperty(c.property_id) && (
                            <DropdownMenuItem asChild>
                              <a 
                                href={getGoogleMapsUrl(getProperty(c.property_id))} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="flex items-center cursor-pointer"
                              >
                                <ExternalLink className="w-4 h-4 mr-2" />
                                Open in Maps
                              </a>
                            </DropdownMenuItem>
                          )}
                          {hasRole('admin') && (
                            <DropdownMenuItem 
                              className="text-destructive focus:text-destructive cursor-pointer"
                              onSelect={() => handleDelete(c.id)}
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Verwijderen
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Weet je het zeker?</AlertDialogTitle>
            <AlertDialogDescription>
              Weet je zeker dat je deze reiniging wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setCleaningToDelete(null)}>Annuleren</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Verwijderen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Google Calendar Popup with Confirmation Email Option */}
      <Dialog open={calendarPopupOpen} onOpenChange={setCalendarPopupOpen}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarIcon className="w-5 h-5 text-primary" />
              Naar Google Calendar
            </DialogTitle>
            <DialogDescription>
              Wil je een bevestigingsmail sturen naar de klant?
            </DialogDescription>
          </DialogHeader>
          
          {selectedCleaningForCalendar && (
            <div className="space-y-4">
              {/* Appointment Summary */}
              <div className="rounded-lg border bg-muted/30 p-4 space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <CalendarIcon className="w-4 h-4 text-muted-foreground" />
                  <span className="font-medium">
                    {formatDate(selectedCleaningForCalendar.cleaning_date)}
                    {selectedCleaningForCalendar.cleaning_date?.includes('T') && (
                      <span className="ml-2 text-muted-foreground">
                        om {selectedCleaningForCalendar.cleaning_date.split('T')[1]?.substring(0, 5)}
                      </span>
                    )}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Building2 className="w-4 h-4 text-muted-foreground" />
                  <span>{getPropertyAddress(selectedCleaningForCalendar.property_id)}</span>
                </div>
                {getCustomerForProperty(selectedCleaningForCalendar.property_id) && (
                  <div className="flex items-center gap-2 text-sm">
                    <User className="w-4 h-4 text-muted-foreground" />
                    <span>
                      {getCustomerForProperty(selectedCleaningForCalendar.property_id)?.first_name}{' '}
                      {getCustomerForProperty(selectedCleaningForCalendar.property_id)?.last_name}
                    </span>
                  </div>
                )}
                {getPanelCount(selectedCleaningForCalendar.property_id) > 0 && (
                  <div className="flex items-center gap-2 text-sm">
                    <Sparkles className="w-4 h-4 text-muted-foreground" />
                    <span>{getPanelCount(selectedCleaningForCalendar.property_id)} zonnepanelen</span>
                  </div>
                )}
              </div>

              {/* Email Checkbox */}
              <div className="flex items-start space-x-3 p-4 rounded-lg border bg-blue-500/5 border-blue-500/20">
                <Checkbox
                  id="send-confirmation"
                  checked={sendConfirmationEmail}
                  onCheckedChange={setSendConfirmationEmail}
                  className="mt-0.5"
                />
                <div className="flex-1">
                  <Label 
                    htmlFor="send-confirmation" 
                    className="text-sm font-medium cursor-pointer flex items-center gap-2"
                  >
                    <Mail className="w-4 h-4 text-blue-500" />
                    Bevestigingsmail klaarzetten
                  </Label>
                  <p className="text-xs text-muted-foreground mt-1">
                    Stuurt alle klantgegevens en afspraakdetails naar n8n om een bevestigingsmail op te stellen
                  </p>
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            <Button 
              variant="outline" 
              onClick={() => {
                setCalendarPopupOpen(false);
                setSelectedCleaningForCalendar(null);
              }}
            >
              Annuleren
            </Button>
            <Button 
              onClick={handleCalendarConfirm}
              disabled={sendingEmail}
              className="gap-2"
            >
              {sendingEmail ? (
                <>
                  <span className="animate-spin">⏳</span>
                  Verzenden...
                </>
              ) : (
                <>
                  <CalendarIcon className="w-4 h-4" />
                  Doorgaan
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reschedule Cancelled Cleaning Dialog */}
      <Dialog open={reschedulePopupOpen} onOpenChange={setReschedulePopupOpen}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarIcon className="w-5 h-5 text-primary" />
              Afspraak Opnieuw Inplannen
            </DialogTitle>
            <DialogDescription>
              Plan een nieuwe datum en tijd voor deze reiniging
            </DialogDescription>
          </DialogHeader>
          
          {cleaningToReschedule && (
            <div className="space-y-4">
              {/* Klant info */}
              <div className="rounded-lg border bg-muted/30 p-3">
                <p className="font-medium">
                  {(() => {
                    const prop = properties.find(p => p.id === cleaningToReschedule.property_id);
                    const cust = prop ? customers.find(c => c.id === prop.customer_id) : null;
                    return cust ? `${cust.first_name} ${cust.last_name}` : 'Onbekende klant';
                  })()}
                </p>
                <p className="text-sm text-muted-foreground">
                  {getPropertyAddress(cleaningToReschedule.property_id)}
                </p>
                {cleaningToReschedule.cancel_reason && (
                  <p className="text-xs text-red-400 mt-2">
                    Vorige reden uitstel: {cleaningToReschedule.cancel_reason}
                  </p>
                )}
              </div>

              {/* Datum en tijd */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Datum *</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start text-left font-normal">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {rescheduleData.cleaning_date ? format(parse(rescheduleData.cleaning_date, 'yyyy-MM-dd', new Date()), 'd MMMM yyyy', { locale: nl }) : 'Selecteer datum'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={rescheduleData.cleaning_date ? parse(rescheduleData.cleaning_date, 'yyyy-MM-dd', new Date()) : undefined}
                        onSelect={(date) => date && setRescheduleData(prev => ({ ...prev, cleaning_date: format(date, 'yyyy-MM-dd') }))}
                        locale={nl}
                        disabled={(date) => date < new Date()}
                        initialFocus
                        components={{
                          DayContent: ({ date }) => <DayWithTooltip date={date} />
                        }}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-2">
                  <Label>Tijd</Label>
                  <Input
                    type="time"
                    value={rescheduleData.cleaning_time}
                    onChange={(e) => setRescheduleData(prev => ({ ...prev, cleaning_time: e.target.value }))}
                  />
                </div>
              </div>

              {/* Bevestigingsmail checkbox */}
              <div className="flex items-start space-x-3 p-4 rounded-lg border bg-blue-500/5 border-blue-500/20">
                <Checkbox
                  id="send-reschedule-confirmation"
                  checked={rescheduleData.send_confirmation}
                  onCheckedChange={(checked) => setRescheduleData(prev => ({ ...prev, send_confirmation: checked }))}
                  className="mt-0.5"
                />
                <div className="flex-1">
                  <Label 
                    htmlFor="send-reschedule-confirmation" 
                    className="text-sm font-medium cursor-pointer flex items-center gap-2"
                  >
                    <Mail className="w-4 h-4 text-blue-500" />
                    Bevestigingsmail klaarzetten naar klant
                  </Label>
                  <p className="text-xs text-muted-foreground mt-1">
                    Stuurt een email naar de klant met de nieuwe afspraakdatum
                  </p>
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            <Button 
              variant="outline" 
              onClick={() => {
                setReschedulePopupOpen(false);
                setCleaningToReschedule(null);
              }}
            >
              Annuleren
            </Button>
            <Button 
              onClick={handleRescheduleSubmit}
              disabled={rescheduling || !rescheduleData.cleaning_date}
              className="gap-2"
            >
              {rescheduling ? (
                <>
                  <span className="animate-spin">⏳</span>
                  Inplannen...
                </>
              ) : (
                <>
                  <CalendarIcon className="w-4 h-4" />
                  Afspraak Bevestigen
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Existing Cleaning Dialog */}
      <Dialog open={editPopupOpen} onOpenChange={setEditPopupOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-primary" />
              Afspraak Bewerken
            </DialogTitle>
            <DialogDescription>
              Pas de datum en tijd aan van deze reiniging
            </DialogDescription>
          </DialogHeader>
          
          {cleaningToEdit && (
            <div className="space-y-4">
              {/* Klant info */}
              <div className="rounded-lg border bg-muted/30 p-3">
                <p className="font-medium">
                  {(() => {
                    const prop = properties.find(p => p.id === cleaningToEdit.property_id);
                    const cust = prop ? customers.find(c => c.id === prop.customer_id) : null;
                    return cust ? `${cust.first_name} ${cust.last_name}` : 'Onbekende klant';
                  })()}
                </p>
                <p className="text-sm text-muted-foreground">
                  {getPropertyAddress(cleaningToEdit.property_id)}
                </p>
              </div>

              {/* Datum en tijd */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Datum *</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start text-left font-normal">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {editData.cleaning_date ? format(parse(editData.cleaning_date, 'yyyy-MM-dd', new Date()), 'd MMMM yyyy', { locale: nl }) : 'Selecteer datum'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={editData.cleaning_date ? parse(editData.cleaning_date, 'yyyy-MM-dd', new Date()) : undefined}
                        onSelect={(date) => date && setEditData(prev => ({ ...prev, cleaning_date: format(date, 'yyyy-MM-dd') }))}
                        locale={nl}
                        initialFocus
                        components={{
                          DayContent: ({ date }) => <DayWithTooltip date={date} />
                        }}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-2">
                  <Label>Tijd</Label>
                  <Input
                    type="time"
                    value={editData.cleaning_time}
                    onChange={(e) => setEditData(prev => ({ ...prev, cleaning_time: e.target.value }))}
                  />
                </div>
              </div>

              {/* Notes/Opmerkingen veld */}
              <div className="space-y-2">
                <Label>Opmerkingen</Label>
                <Textarea
                  value={editData.notes}
                  onChange={(e) => setEditData(prev => ({ ...prev, notes: e.target.value }))}
                  rows={3}
                  placeholder="Extra opmerkingen voor de kuisman..."
                />
              </div>

              {/* Foto's & documenten */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Paperclip className="w-4 h-4" />
                  Foto's & documenten ({editPhotos.length})
                </Label>
                {editPhotos.length > 0 && (
                  <div className="grid grid-cols-3 gap-2" data-testid="cleaning-photos-grid">
                    {editPhotos.map(p => (
                      <div key={p.id} className="relative group">
                        <button
                          type="button"
                          onClick={() => setLightboxDoc({ url: p.url, filename: p.filename || 'Foto' })}
                          className="w-full focus:outline-none"
                        >
                          {/\.pdf($|\?)/i.test(p.url || '') || /\.pdf$/i.test(p.filename || '') ? (
                            <div className="w-full h-20 rounded-md border bg-muted/40 flex flex-col items-center justify-center gap-1 hover:bg-muted transition-colors">
                              <FileText className="w-6 h-6 text-red-500" />
                              <span className="text-[10px] text-muted-foreground truncate max-w-full px-1">{p.filename || 'PDF'}</span>
                            </div>
                          ) : (
                            <img
                              src={p.url}
                              alt={p.filename || 'Foto'}
                              className="w-full h-20 object-cover rounded-md border hover:opacity-90 transition-opacity"
                            />
                          )}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleEditPhotoDelete(p.id)}
                          className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                          data-testid={`cleaning-photo-delete-${p.id}`}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <label className="inline-flex">
                  <input
                    type="file"
                    accept="image/*,.pdf"
                    className="hidden"
                    disabled={uploadingEditPhoto}
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) handleEditPhotoUpload(f);
                      e.target.value = '';
                    }}
                    data-testid="cleaning-photo-input"
                  />
                  <span className={`inline-flex items-center gap-2 text-sm border rounded-md px-3 py-1.5 cursor-pointer hover:bg-muted transition-colors ${uploadingEditPhoto ? 'opacity-50 pointer-events-none' : ''}`}>
                    {uploadingEditPhoto ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                    {uploadingEditPhoto ? 'Uploaden...' : 'Foto / document toevoegen'}
                  </span>
                </label>
              </div>

              {/* Bevestigingsmail checkbox */}
              <div className="flex items-start space-x-3 p-4 rounded-lg border bg-blue-500/5 border-blue-500/20">
                <Checkbox
                  id="send-edit-confirmation"
                  checked={editData.send_confirmation}
                  onCheckedChange={(checked) => setEditData(prev => ({ ...prev, send_confirmation: checked }))}
                  className="mt-0.5"
                />
                <div className="flex-1">
                  <Label 
                    htmlFor="send-edit-confirmation" 
                    className="text-sm font-medium cursor-pointer flex items-center gap-2"
                  >
                    <Mail className="w-4 h-4 text-blue-500" />
                    Nieuwe bevestigingsmail verzenden
                  </Label>
                  <p className="text-xs text-muted-foreground mt-1">
                    Stuurt een email naar de klant met de gewijzigde afspraakdatum
                  </p>
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            <Button 
              variant="outline" 
              onClick={() => {
                setEditPopupOpen(false);
                setCleaningToEdit(null);
              }}
            >
              Annuleren
            </Button>
            <Button 
              onClick={handleEditSubmit}
              disabled={editing || !editData.cleaning_date}
              className="gap-2"
            >
              {editing ? (
                <>
                  <span className="animate-spin">⏳</span>
                  Opslaan...
                </>
              ) : (
                <>
                  <Edit className="w-4 h-4" />
                  Wijzigingen Opslaan
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Customer Details Popup */}
      <Dialog open={customerPopupOpen} onOpenChange={setCustomerPopupOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="w-5 h-5 text-primary" />
              Klantgegevens
            </DialogTitle>
            <DialogDescription>
              Contactgegevens voor deze afspraak
            </DialogDescription>
          </DialogHeader>
          
          {selectedCustomerData && (
            <div className="space-y-4">
              {/* Customer Name */}
              <div className="p-4 rounded-lg border bg-card">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                    <User className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">
                      {selectedCustomerData.customer?.first_name} {selectedCustomerData.customer?.last_name}
                    </h3>
                    {selectedCustomerData.customer?.company_name && (
                      <p className="text-sm text-muted-foreground">{selectedCustomerData.customer.company_name}</p>
                    )}
                  </div>
                </div>

                <div className="space-y-3">
                  {/* Phone - Clickable */}
                  {selectedCustomerData.customer?.phone && (
                    <a 
                      href={`tel:${selectedCustomerData.customer.phone.replace(/\s/g, '')}`}
                      className="flex items-center gap-3 p-3 rounded-lg bg-green-500/10 border border-green-500/30 hover:bg-green-500/20 transition-colors"
                    >
                      <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                        <Phone className="w-5 h-5 text-green-500" />
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Telefoon</p>
                        <p className="font-medium text-green-500">{formatPhoneNumber(selectedCustomerData.customer.phone)}</p>
                      </div>
                    </a>
                  )}

                  {/* Email - Clickable */}
                  {selectedCustomerData.customer?.email && (
                    <a 
                      href={`mailto:${selectedCustomerData.customer.email}`}
                      className="flex items-center gap-3 p-3 rounded-lg bg-blue-500/10 border border-blue-500/30 hover:bg-blue-500/20 transition-colors"
                    >
                      <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                        <Mail className="w-5 h-5 text-blue-500" />
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Email</p>
                        <p className="font-medium text-blue-500">{selectedCustomerData.customer.email}</p>
                      </div>
                    </a>
                  )}

                  {/* Address with Maps/Waze choice */}
                  {selectedCustomerData.property && (
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-orange-500/10 border border-orange-500/30">
                      <div className="w-10 h-10 rounded-full bg-orange-500/20 flex items-center justify-center">
                        <Building2 className="w-5 h-5 text-orange-500" />
                      </div>
                      <div className="flex-1">
                        <p className="text-xs text-muted-foreground">Adres</p>
                        <p className="font-medium text-orange-500">
                          {selectedCustomerData.property.street} {selectedCustomerData.property.number}
                        </p>
                        <p className="text-sm text-orange-400/70">
                          {selectedCustomerData.property.postal_code} {selectedCustomerData.property.city}
                        </p>
                      </div>
                      <div className="flex flex-col gap-1">
                        <a 
                          href={getGoogleMapsUrl(selectedCustomerData.property)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs px-2 py-1 rounded bg-orange-500/20 hover:bg-orange-500/30 text-orange-400 transition-colors"
                        >
                          Google Maps
                        </a>
                        <a 
                          href={getWazeUrl(selectedCustomerData.property)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs px-2 py-1 rounded bg-orange-500/20 hover:bg-orange-500/30 text-orange-400 transition-colors"
                        >
                          Waze
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Original cancellation reason */}
              {selectedCustomerData.cleaning?.cancel_reason && (
                <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30">
                  <p className="text-xs text-muted-foreground mb-1">Reden uitstel:</p>
                  <p className="text-sm text-red-400">{selectedCustomerData.cleaning.cancel_reason}</p>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            <Button 
              variant="outline" 
              onClick={() => setCustomerPopupOpen(false)}
            >
              Sluiten
            </Button>
            <Button 
              onClick={() => {
                setCustomerPopupOpen(false);
                if (selectedCustomerData?.property?.id) {
                  navigate(`/properties/${selectedCustomerData.property.id}`);
                }
              }}
            >
              Naar klantpagina
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Popup viewer voor foto's & documenten */}
      <DocumentLightbox
        open={!!lightboxDoc}
        onOpenChange={(o) => { if (!o) setLightboxDoc(null); }}
        document={lightboxDoc}
      />
    </div>
  );
};

export default Cleaning;
